import React, { useState } from 'react'
import {
  Checkbox,
  Button,
  InputText,
  DatePicker,
  Label,
  Select,
  InputButton,
  cn
} from '@pcln/horizon'

import { Discount } from 'pcln-icons'
import useBootstrapData from '@/hooks/useBootstrapData'
import { generateTimeSelectOptions } from '@/components/TimeSelect/TimeSelect'
import { getGridContainerClass, getInputClass } from './HorizonCarForm.variants'
import { PLACEHOLDERS, LABELS, DEFAULT_VALUES } from './constants'
import { createToggleHandler } from './utils/formHandlers'

export default function HorizonCarForm() {
  const { locale: { localeCode = 'en-US' } = {} } = useBootstrapData()
  const [addHotel, setAddHotel] = useState<boolean>(DEFAULT_VALUES.ADD_HOTEL)
  const [addFlight, setAddFlight] = useState<boolean>(DEFAULT_VALUES.ADD_FLIGHT)
  const [differentDropOff, setDifferentDropOff] = useState<boolean>(
    DEFAULT_VALUES.DIFFERENT_DROPOFF
  )
  const [pickupTime, setPickupTime] = useState<string>(
    DEFAULT_VALUES.PICKUP_TIME
  )
  const [dropoffTime, setDropoffTime] = useState<string>(
    DEFAULT_VALUES.DROPOFF_TIME
  )
  const hasPackageOption = true
  const isPackage = addHotel || addFlight

  return (
    <div className="w-auto">
      <form
        className="w-auto"
        aria-label="horizon-car-search-form"
        onSubmit={e => e.preventDefault()}
      >
        <div className="w-auto mx-auto">
          <div
            className={getGridContainerClass(
              differentDropOff,
              addHotel,
              addFlight
            )}
          >
            {!isPackage && (
              <div className={getInputClass('options')}>
                {/* Dropoff type checkbox */}
                <Label htmlFor="differentDropOff" className="text-sm">
                  <Checkbox
                    name="differentDropOff"
                    id="differentDropOff"
                    value="differentDropOff"
                    checked={differentDropOff}
                    handleChange={createToggleHandler(
                      differentDropOff,
                      setDifferentDropOff
                    )}
                  />
                  <span className="ml-2">{LABELS.DIFFERENT_DROPOFF}</span>
                </Label>
              </div>
            )}

            {!addFlight ? (
              <>
                {/* Pickup Location Input */}
                <InputText
                  label={
                    differentDropOff
                      ? LABELS.PICKUP_LOCATION
                      : LABELS.DEFAULT_PICKUP_LOCATION
                  }
                  isLabelHidden
                  iconLeft="search"
                  className={getInputClass('pickup', differentDropOff)}
                  placeholder={
                    differentDropOff
                      ? PLACEHOLDERS.PICKUP_LOCATION
                      : PLACEHOLDERS.DEFAULT_PICKUP_LOCATION
                  }
                />
                {/* Dropoff Location Input */}
                {differentDropOff && !isPackage && (
                  <InputText
                    label={LABELS.DROPOFF_LOCATION}
                    isLabelHidden
                    iconLeft="search"
                    className={getInputClass('dropoff')}
                    placeholder={PLACEHOLDERS.DROPOFF_LOCATION}
                  />
                )}
              </>
            ) : (
              <>
                <InputText
                  label="Origin"
                  isLabelHidden
                  iconLeft="search"
                  placeholder={PLACEHOLDERS.ORIGIN}
                  className="col-span-full sm:[grid-area:unset]"
                />
                <InputText
                  label="Destination"
                  isLabelHidden
                  iconLeft="search"
                  placeholder={PLACEHOLDERS.DESTINATION}
                  className="col-span-full sm:[grid-area:unset]"
                />
              </>
            )}

            {!isPackage ? (
              <>
                {/* Pickup date */}
                <div className="**:translate-y-0">
                  <DatePicker
                    label={LABELS.PICKUP_DATE}
                    mode="single"
                    numberOfMonths={1}
                    handleCalendarChange={() => console.log('Calendar changed')}
                  />
                </div>

                {/* Pickup time */}
                <Select
                  label={LABELS.PICKUP_TIME}
                  isInset
                  menuItems={generateTimeSelectOptions(localeCode, true)}
                  initialValue={pickupTime}
                  onChange={setPickupTime}
                  isLabelHidden
                  placeholder={LABELS.PICKUP_TIME}
                />

                {/* Dropoff date */}
                <div className="**:translate-y-0">
                  <DatePicker
                    label={LABELS.DROPOFF_DATE}
                    mode="single"
                    numberOfMonths={1}
                    handleCalendarChange={() => console.log('Calendar changed')}
                  />
                </div>

                {/* Dropoff time */}
                <Select
                  label={LABELS.DROPOFF_TIME}
                  isInset
                  menuItems={generateTimeSelectOptions(localeCode, true)}
                  initialValue={dropoffTime}
                  onChange={setDropoffTime}
                  isLabelHidden
                  placeholder={LABELS.DROPOFF_TIME}
                />
              </>
            ) : (
              <>
                <div className="**:translate-y-0">
                  <DatePicker
                    className="flight-dates"
                    title="Departing – Returning"
                    label="Departing – Returning"
                    mode="range"
                    numberOfMonths={2}
                    handleCalendarChange={() => console.log('Calendar changed')}
                  />
                </div>
                {addHotel ? (
                  <InputButton
                    label={LABELS.PASSENGERS_WITH_HOTEL}
                    isLabelHidden
                    iconLeft="person"
                    className="**:translate-y-0"
                    placeholder={PLACEHOLDERS.PASSENGERS_WITH_HOTEL}
                  />
                ) : (
                  <InputButton
                    label={LABELS.PASSENGERS}
                    iconLeft="person"
                    type="button"
                    className="**:translate-y-0"
                    isLabelHidden
                    placeholder={PLACEHOLDERS.PASSENGERS}
                  />
                )}
              </>
            )}

            {hasPackageOption && (
              <div
                className={cn(
                  getInputClass('package'),
                  isPackage && 'sm:col-span-1'
                )}
              >
                <span>
                  <Discount m={1} mr={2} color="success" />
                  {LABELS.PACKAGE_DEALS}
                </span>
                <Label htmlFor="addHotel">
                  <Checkbox
                    name="addHotel"
                    id="addHotel"
                    value="addHotel"
                    checked={addHotel}
                    handleChange={createToggleHandler(addHotel, setAddHotel)}
                  />
                  <span className="ml-2">{LABELS.ADD_HOTEL}</span>
                </Label>
                <Label htmlFor="addFlight">
                  <Checkbox
                    name="addFlight"
                    id="addFlight"
                    value="addFlight"
                    checked={addFlight}
                    handleChange={createToggleHandler(addFlight, setAddFlight)}
                  />
                  <span className="ml-2">{LABELS.ADD_FLIGHT}</span>
                </Label>
              </div>
            )}

            {/* Search Button */}
            <Button
              type="primary"
              size="lg"
              className={cn(
                getInputClass('submit'),
                isPackage && hasPackageOption && 'sm:col-span-1',
                !hasPackageOption && 'sm:col-span-full'
              )}
            >
              {PLACEHOLDERS.SEARCH_BUTTON}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
